import React, { useContext, useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { Paper, TextField } from '@uhc-tempo/components/node_modules/@mui/material';
import { Autocomplete, Box, IconButton, InputAdornment } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import '@uhc-tempo/components/tempo.css';
import styles from '../../../styles/PageStyles.js';
import theme from '../../../styles/PageTheme';
import { FormContext } from '../../../context/Form.context';
import './Search.page.css';
import './SearchBar.component.css';
import { getPrimarySelectedService, formatCarePathResponse } from '../../../utils/carePathUtil';
import CarePathService from '../../../API/CarePathService.js';
import ServiceSearchErrorCard from './ServiceSearchErrorCard.component';
import {
    checkForRevcodeOrVisionMaterialsServiceType,
    shouldShowDentalPolicyDisclaimer,
    checkForPHCalifornia,
    checkBehavioralMedicalDisclaimerCondition,
    checkForRevNoEstimateServiceType,
    checkIfMemberHasVisionCoverageType,
    checkForVisionMaterialsServiceType,
    checkForNoBenefitsVisionServiceType,
    checkForLowUsageCodeServiceType,
} from '../../../utils/SearchPageUtil.js';
import RevCodesDialog from './RevCodesDialog.js';
import { ENABLE_COVERAGE_TYPES } from '../../../constants/index';

export default function ServiceSearchInput(props) {
    const classes = styles(theme);
    const {
        formValues,
        handleChangeCustom,
        memberDetails,
        getIncrementedCorId,
        clearEstimateHistory,
    } = useContext(FormContext);
    const { locationOfService, selectedService } = formValues;
    const [displayRevCodePopup, setDisplayRevCodePopup] = useState(false);
    const {
        setNoCoverageDisclaimer,
        setInnNoNetworkDisclaimer,
        openLocationPopper,
        message,
        setMessage,
        customPopper,
        setCustomPopper,
        setServiceFocus,
        service,
        locationInputRef,
        setServiceTypeDisclaimer,
        setDentalPolicyDisclaimer,
        setDisableProviderDropdown,
    } = props;

    useEffect(() => {
        if (
            selectedService?.value?.hasBillingCodeGroup &&
            !checkForRevNoEstimateServiceType(selectedService?.value?.fullCarePath?.serviceTypes)
        ) {
            setDisplayRevCodePopup(true);
        } else setDisplayRevCodePopup(false);
    }, [selectedService]);

    const handleCloseReveCodeDialog = () => {
        if (selectedService?.value?.hasBillingCodeGroup) {
            setNoCoverageDisclaimer(false);
            setInnNoNetworkDisclaimer(false);
            setServiceTypeDisclaimer(false);
            setDentalPolicyDisclaimer(false);
            setDisableProviderDropdown(false);
            handleChangeCustom(null, SELECTED_SERVICE);
        }
        setDisplayRevCodePopup(false);
    };

    const [serviceSearchLoading, setServiceSearchLoading] = useState(false);
    const [serviceOptions, updateServiceOptions] = useState([]);
    const [noServiceExists, setNoServiceExists] = useState(false);

    const SELECTED_SERVICE = 'selectedService';

    const handleServiceSearchInputChange = async (inputServiceStr) => {
        if (inputServiceStr) {
            try {
                const serviceSearchOptions = await CarePathService.getCarepathSearchResponse(
                    memberDetails,
                    inputServiceStr,
                    getIncrementedCorId()
                );
                setStateBasedOnSearchResults(
                    false,
                    serviceSearchOptions,
                    false,
                    selectedService.value == null
                );
            } catch (error) {
                await setStateBasedOnSearchResults(true, [], false, selectedService.value == null);
            }
        } else {
            await setStateBasedOnSearchResults(false, [], false, false);
        }
    };

    const debouncedServiceChange = debounce(handleServiceSearchInputChange, 1000);

    const setStateBasedOnSearchResults = (
        noService,
        serviceSearchOptions,
        loading,
        dropdownOpen
    ) => {
        setNoServiceExists(noService);
        updateServiceOptions(serviceSearchOptions);
        setServiceSearchLoading(loading);
        setCustomPopper(dropdownOpen);
    };

    const handleSelectedServiceChange = async (newSelectedServiceValue) => {
        setNoCoverageDisclaimer(false);
        setInnNoNetworkDisclaimer(false);
        setServiceTypeDisclaimer(false);
        setDentalPolicyDisclaimer(false);
        setDisableProviderDropdown(false);
        if (newSelectedServiceValue == null || newSelectedServiceValue == '') {
            handleChangeCustom(null, SELECTED_SERVICE);
        } else {
            const isCoverageTypesEnabled = ENABLE_COVERAGE_TYPES;
            const { primarySelectedService, showDentalPolicyDisclaimer, isNonPHCalifornia } =
                await getDataForSelectedService(newSelectedServiceValue);
            handleChangeCustom(primarySelectedService, SELECTED_SERVICE);
            if (showDentalPolicyDisclaimer) {
                setDentalPolicyDisclaimer(true);
                setDisableProviderDropdown(true);
            } else if (
                checkForLowUsageCodeServiceType(primarySelectedService?.fullCarePath?.serviceTypes)
            ) {
                setServiceTypeDisclaimer(true);
                setDisableProviderDropdown(true);
            } else if (
                checkForNoBenefitsVisionServiceType(
                    primarySelectedService?.fullCarePath?.serviceTypes
                )
            ) {
                setServiceTypeDisclaimer(true);
                setDisableProviderDropdown(true);
            } else if (
                checkForVisionMaterialsServiceType(
                    primarySelectedService?.fullCarePath?.serviceTypes
                )
            ) {
                if (!checkIfMemberHasVisionCoverageType(memberDetails)) {
                    setNoCoverageDisclaimer(true);
                }
                setDisableProviderDropdown(true);
            } else if (
                checkForRevcodeOrVisionMaterialsServiceType(
                    primarySelectedService?.fullCarePath?.serviceTypes
                )
            ) {
                if (
                    !primarySelectedService?.hasBillingCodeGroup ||
                    checkForRevNoEstimateServiceType(
                        primarySelectedService?.fullCarePath?.serviceTypes
                    )
                ) {
                    setServiceTypeDisclaimer(true);
                }
                setDisableProviderDropdown(true);
            } else if (isNonPHCalifornia && !isCoverageTypesEnabled) {
                setNoCoverageDisclaimer(true);
                setDisableProviderDropdown(true);
            } else if (
                checkBehavioralMedicalDisclaimerCondition(primarySelectedService?.fullCarePath)
            ) {
                setServiceTypeDisclaimer(true);
                setDisableProviderDropdown(true);
            }
            setTimeout(() => {
                if (!locationOfService.value) {
                    locationInputRef.current.focus();
                    openLocationPopper();
                }
            }, 200);
            setMessage(false);
            setCustomPopper(false);
        }
    };

    const getDataForSelectedService = async (newSelectedServiceValue) => {
        if (newSelectedServiceValue.carePathId) {
            try {
                const response = await CarePathService.getCarepathDataResponse(
                    memberDetails,
                    newSelectedServiceValue,
                    getIncrementedCorId()
                );
                let primarySelectedService = {};
                const { showDentalPolicyDisclaimer } = shouldShowDentalPolicyDisclaimer(
                    response.coverageTypeCode,
                    memberDetails
                );
                const isNonPHCalifornia = checkForPHCalifornia(response, memberDetails);
                if (
                    checkForRevcodeOrVisionMaterialsServiceType(response.serviceTypes) ||
                    showDentalPolicyDisclaimer ||
                    checkBehavioralMedicalDisclaimerCondition(response)
                ) {
                    primarySelectedService = formatCarePathResponse(response);
                } else {
                    primarySelectedService = getPrimarySelectedService(response);
                }
                primarySelectedService.hasBillingCodeGroup =
                    newSelectedServiceValue?.hasBillingCodeGroup;
                clearEstimateHistory();
                return { primarySelectedService, showDentalPolicyDisclaimer, isNonPHCalifornia };
            } catch (error) {
                console.error('Error occurred when retrieving care path data', error);
                handleChangeCustom(null, SELECTED_SERVICE);
                await setStateBasedOnSearchResults(false, [], false, false);
                return {};
            }
        }
    };

    const noServiceOptionsMessage = () => {
        if (noServiceExists && serviceOptions.length == 0 && !serviceSearchLoading) {
            return (
                <div>
                    <span style={{ color: '#D32F2F' }}>No service found</span>
                    <br />
                    <span style={{ fontSize: '15px' }}>
                        Search for the covered item or service to be performed (Example: MRI Leg,
                        Knee Replacement).
                        <br />
                        <br />
                        Obtain the billing code for item or service to be performed (Example: CPT
                        99213, DRG 29888 or HCPC K1093).
                    </span>
                </div>
            );
        }
    };

    const loadingText = () => {
        return (
            <div
                aria-live="polite" //announce loading state
            >
                Loading...
            </div>
        );
    };

    const clearSelectedServiceButton = () => {
        return (
            <IconButton
                sx={classes.clearSelectedServiceButton}
                aria-label="clearServiceInput"
                data-testid="clearServiceInput"
                onClick={() => {
                    setServiceTypeDisclaimer(false);
                    setDentalPolicyDisclaimer(false);
                    setDisableProviderDropdown(false);
                    handleChangeCustom(null, SELECTED_SERVICE);
                    handleChangeCustom(null, 'selectedProvider');
                    handleChangeCustom(null, 'providerForFacilityCharges');
                    setServiceFocus(true);
                }}
            >
                <CloseIcon data-testid="clearServiceInput" />
            </IconButton>
        );
    };

    const ServiceSearchErrorPopup = (props) => {
        const optionsAvailable = serviceOptions?.length > 0;
        return (
            <Paper elevation={1} sx={classes.width100}>
                {message && (
                    <ServiceSearchErrorCard classes={classes} optionsAvailable={optionsAvailable} />
                )}
                {(!message || optionsAvailable || serviceSearchLoading) && (
                    <Paper elevation={0} sx={classes.width100} {...props} />
                )}
            </Paper>
        );
    };

    return (
        <React.Fragment>
            <Paper style={classes.serviceInputPaper} variant="outlined">
                <Autocomplete
                    id="auto-complete-serviceInput"
                    size="small"
                    variant="standard"
                    label="Services, treatments, procedures, or billing code"
                    data-testid="serviceInput"
                    disablePortal
                    value={selectedService.value}
                    key={selectedService.value}
                    onChange={async (event, newSelectedServiceValue) => {
                        await handleSelectedServiceChange(newSelectedServiceValue);
                    }}
                    filterOptions={(options, state) => {
                        return serviceOptions;
                    }}
                    options={serviceOptions}
                    getOptionLabel={(option) => {
                        return ` ${option.name}`;
                    }}
                    loading={serviceSearchLoading}
                    loadingText={loadingText()}
                    onInputChange={async (event, newSelectedServiceValue) => {
                        setNoServiceExists(false);
                        if (event?.type == 'change') {
                            if (newSelectedServiceValue != '') {
                                setServiceSearchLoading(true);
                                setCustomPopper(true);
                            }
                            debouncedServiceChange(newSelectedServiceValue);
                        }
                    }}
                    isOptionEqualToValue={(option, value) => option.name === value.name}
                    renderOption={(props, option, state) => (
                        <li {...props}>
                            <Box mr={1}>{state.selected && <CheckIcon />}</Box>
                            <Box sx={{ marginLeft: state.selected ? '' : '22px' }}>
                                {option.name}
                            </Box>
                        </li>
                    )}
                    noOptionsText={!message && noServiceOptionsMessage()}
                    open={customPopper}
                    onClose={() => setCustomPopper(false)}
                    PaperComponent={ServiceSearchErrorPopup}
                    onBlur={() => setCustomPopper(false)}
                    clearOnBlur={false}
                    renderInput={(params) => (
                        <TextField
                            variant="standard"
                            inputRef={service}
                            onChange={(event) => {
                                setMessage(false);
                            }}
                            focused={true}
                            sx={classes.serviceInputTextField}
                            {...params}
                            label="Services, treatments, procedures, or billing code"
                            InputProps={{
                                ...params.InputProps,
                                style: classes.serviceInput,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {selectedService.value ? clearSelectedServiceButton() : ''}
                                    </InputAdornment>
                                ),
                                disableUnderline: true,
                            }}
                        />
                    )}
                />
            </Paper>
            <RevCodesDialog
                onServiceSelect={async (newSelectedServiceValue) => {
                    handleSelectedServiceChange(newSelectedServiceValue);
                }}
                displayPopup={displayRevCodePopup}
                onClose={handleCloseReveCodeDialog}
            />
        </React.Fragment>
    );
}
